/** Helpers for PF / ESI / PT contribution statements + portal filing files. */

export function round2(n) {
  return Math.round(Number(n || 0) * 100) / 100;
}

/** Round to nearest rupee for statutory filing / challan amounts. */
export function roundRupee(n) {
  return Math.round(Number(n || 0));
}

export function amountByName(paidByName, matchers) {
  if (!paidByName || typeof paidByName !== "object") return 0;
  const keys = Object.keys(paidByName);
  for (const key of keys) {
    const n = String(key || "").toLowerCase();
    if (matchers.some((m) => n.includes(String(m).toLowerCase()))) {
      return round2(Math.abs(Number(paidByName[key]) || 0));
    }
  }
  return 0;
}

export function pfEmployeeAmount(row) {
  return amountByName(row?.paidByName, [
    "pf (employee)",
    "provident fund (employee)",
    "epf (employee)",
  ]);
}

export function esiEmployeeAmount(row) {
  const fromPaid = amountByName(row?.paidByName, [
    "esi (employee)",
    "esic (employee)",
  ]);
  if (fromPaid > 0) return fromPaid;
  return round2(Number(row?.employerStatutory?.esiEmployee) || 0);
}

export function ptAmount(row) {
  return amountByName(row?.paidByName, ["professional tax", "pt ("]);
}

export function payMonthNow() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function buildPfRows(registerRows = []) {
  return (registerRows || [])
    .map((r) => {
      const empPf = pfEmployeeAmount(r);
      const es = r.employerStatutory || {};
      const flags = r.statutoryFlags || {};
      const epsOn = flags.epsApplicable === true;
      const employerShare = round2(Number(es.employerShare12) || 0);
      const adminCharges = round2(Number(es.adminCharges) || 0);
      const edli = round2(Number(es.edli) || 0);
      let employerEpf = round2(Number(es.pfEmployerEpf) || 0);
      let eps = round2(Number(es.eps) || 0);
      // Guard older snapshots that still carry EPS when master flag is off
      if (!epsOn && eps > 0.009) {
        employerEpf = round2(employerEpf + eps);
        eps = 0;
      }
      const pfWage = round2(Number(es.pfWageUsed) || 0);
      const storedWage = (v, fallback) =>
        v != null && Number.isFinite(Number(v)) ? round2(Number(v)) : fallback;
      return {
        empCode: r.empCode || "",
        name: String(r.name || "").replace(/#/g, " ").trim(),
        uan: String(r.uan || "").replace(/\D/g, ""),
        pfAccountNo: r.pfAccountNo || "",
        branch: r.branch || "",
        paidDays: Number(r.paidDays) || 0,
        calendarDays: Number(r.calendarDays) || 0,
        lopDays: Number(r.lopDays) || 0,
        grossPay: round2(r.grossPay),
        pfWages: pfWage,
        epsWages: epsOn ? storedWage(es.epsWageUsed, Math.min(pfWage, 15000)) : 0,
        edliWages: storedWage(es.edliWageUsed, Math.min(pfWage, 15000)),
        employeePf: empPf,
        employerEpf,
        eps,
        edli,
        adminCharges,
        employerShare,
        totalPf: round2(empPf + employerShare),
        pfApplicable: flags.pfApplicable !== false && (empPf > 0.009 || employerShare > 0.009),
        epsApplicable: epsOn,
        edliApplicable: edli > 0.009,
      };
    })
    .filter((r) => r.employeePf > 0.009 || r.employerShare > 0.009);
}

/** PF summary metrics for the statement cards (counts + amounts). */
export function buildPfSummaryCards(pfRows = [], adminChargesMinimum = 500) {
  const liability = buildPfLiabilityBreakdown(pfRows, adminChargesMinimum);
  return {
    totalEmployees: (pfRows || []).length,
    pfApplicableCount: (pfRows || []).filter((r) => r.pfApplicable || r.employeePf > 0.009).length,
    epsApplicableCount: (pfRows || []).filter((r) => r.epsApplicable).length,
    edliCount: (pfRows || []).filter((r) => r.edliApplicable || r.edli > 0.009).length,
    pfAmount: liability.employeePf,
    epfAmount: liability.employerEpf,
    epsAmount: liability.account10,
    adminCharges: liability.account2,
    edliAmount: liability.account21,
    totalPayable: liability.totalPayable,
    liability,
  };
}

export function filterRowsByBranch(rows = [], branch = "") {
  const b = String(branch || "").trim();
  if (!b || b === "__ALL__") return rows || [];
  return (rows || []).filter(
    (r) => String(r.branch || "").trim().toLowerCase() === b.toLowerCase()
  );
}

/**
 * Apply establishment-level minimum admin charges (default ₹500).
 * Per-employee admin stays as calculated; payable total is max(sum, minimum).
 */
export function applyPfAdminMinimum(pfRows, adminChargesMinimum = 500) {
  const min = Math.max(0, Number(adminChargesMinimum) || 0);
  const adminCalculated = roundRupee(sumField(pfRows, "adminCharges"));
  const adminPayable = Math.max(adminCalculated, min);
  const adminTopUp = roundRupee(adminPayable - adminCalculated);
  return {
    adminCalculated,
    adminMinimum: min,
    adminPayable,
    adminTopUp,
    appliedMinimum: adminTopUp > 0,
  };
}

/**
 * EPFO challan / liability buckets (rounded ₹).
 * Account 1 = EE PF + ER EPF; Account 10 = EPS; Account 2 = Admin (min applied);
 * Account 21 = EDLI.
 */
export function buildPfLiabilityBreakdown(pfRows, adminChargesMinimum = 500) {
  const admin = applyPfAdminMinimum(pfRows, adminChargesMinimum);
  const employeePf = sumFieldRupee(pfRows, "employeePf");
  const employerEpf = sumFieldRupee(pfRows, "employerEpf");
  const eps = sumFieldRupee(pfRows, "eps");
  const edli = sumFieldRupee(pfRows, "edli");
  const account1 = roundRupee(employeePf + employerEpf);
  const account10 = eps;
  const account2 = admin.adminPayable;
  const account21 = edli;
  const totalPayable = roundRupee(account1 + account10 + account2 + account21);
  return {
    ...admin,
    employeePf,
    employerEpf,
    account1,
    account10,
    account2,
    account21,
    totalPayable,
    lines: [
      {
        label: "PF + EPF (Account No. 1)",
        amount: account1,
        detail: `PF (Employee) ${employeePf} + EPF (Employer) ${employerEpf}`,
      },
      { label: "EPS (Account No. 10)", amount: account10 },
      {
        label: "Admin Charges (Account No. 2)",
        amount: account2,
        detail:
          admin.appliedMinimum
            ? `Calculated ${admin.adminCalculated}; minimum ${admin.adminMinimum} applied`
            : `Calculated ${admin.adminCalculated}`,
      },
      { label: "EDLI (Account No. 21)", amount: account21 },
      { label: "Total PF Payable", amount: totalPayable },
    ],
  };
}

export function buildEsiRows(registerRows = []) {
  return (registerRows || [])
    .map((r) => {
      const empEsi = esiEmployeeAmount(r);
      const es = r.employerStatutory || {};
      const employerEsi = round2(Number(es.esiEmployer) || 0);
      return {
        empCode: r.empCode || "",
        name: r.name || "",
        esiNo: String(r.esiNo || "").trim(),
        branch: r.branch || "",
        paidDays: Number(r.paidDays) || 0,
        lopDays: Number(r.lopDays) || 0,
        grossPay: round2(r.grossPay),
        employeeEsi: empEsi,
        employerEsi,
        totalEsi: round2(empEsi + employerEsi),
        doe: r.doe ? String(r.doe).slice(0, 10) : null,
      };
    })
    .filter((r) => r.employeeEsi > 0.009 || r.employerEsi > 0.009);
}

/** Shift YYYY-MM by delta months. */
export function shiftPayMonth(ym, deltaMonths = -1) {
  const m = String(ym || "").slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(m)) return "";
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(y, mo - 1 + Number(deltaMonths || 0), 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** ESIC MC_Template11 reason codes (Sheet2). */
export const ESIC_REASON_CODES = {
  WITHOUT_REASON: 0,
  ON_LEAVE: 1,
  LEFT_SERVICE: 2,
  RETIRED: 3,
  OUT_OF_COVERAGE: 4,
  EXPIRED: 5,
  NON_IMPLEMENTED_AREA: 6,
  COMPLIANCE_IMMEDIATE_EMPLOYER: 7,
  SUSPENSION: 8,
  STRIKE_LOCKOUT: 9,
  RETRENCHMENT: 10,
  NO_WORK: 11,
  DOESNT_BELONG: 12,
  DUPLICATE_IP: 13,
};

export function formatEsicIpNumber(esiNo) {
  const digits = String(esiNo || "").replace(/\D/g, "");
  return digits.slice(0, 10);
}

/** ESIC template: IP Name — alphabets and spaces only. */
export function formatEsicIpName(name) {
  return String(name || "")
    .replace(/[^A-Za-z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 100);
}

/** ESIC: days must be whole integers (ceil fractions). */
export function formatEsicDays(paidDays) {
  const n = Number(paidDays) || 0;
  if (n <= 0) return 0;
  return Math.ceil(n);
}

/** YYYY-MM-DD → DD/MM/YYYY (zero-padded). */
export function formatEsicLastWorkingDay(isoDate) {
  const s = String(isoDate || "").slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function payMonthOfDate(isoDate) {
  const s = String(isoDate || "").slice(0, 10);
  return /^\d{4}-\d{2}/.test(s) ? s.slice(0, 7) : "";
}

/**
 * Build ESIC MC_Template11 Sheet1 rows for the contribution period.
 * - Current-month ESI employees with wages/days and reason 0 (or exit codes if 0 days).
 * - Prior-month ESI leavers (DOE / relieved in previous pay month) with days=0, wages=0,
 *   reason=2 + LWD so ESIC removes them from the employer's IP list.
 * - masterExitRows: employee-master exits (relievedDate in previous month) — authoritative.
 */
export function buildEsicMcTemplateRows({
  currentEsiRows = [],
  previousEsiRows = [],
  masterExitRows = [],
  payMonth,
} = {}) {
  const ym = String(payMonth || "").slice(0, 7);
  const prevYm = shiftPayMonth(ym, -1);
  const byIp = new Map();

  const upsert = (row) => {
    const ip = formatEsicIpNumber(row.ipNumber);
    if (!ip || ip.length < 10) return;
    byIp.set(ip, {
      ipNumber: ip,
      ipName: formatEsicIpName(row.ipName),
      days: Number(row.days) || 0,
      monthlyWages: roundRupee(row.monthlyWages),
      reasonCode: Number(row.reasonCode) || 0,
      lastWorkingDay: row.lastWorkingDay || "",
      isExit: Boolean(row.isExit),
      empCode: row.empCode || "",
    });
  };

  for (const r of currentEsiRows || []) {
    const ip = formatEsicIpNumber(r.esiNo);
    if (!ip) continue;
    const days = formatEsicDays(r.paidDays);
    const wages = roundRupee(r.grossPay);
    const exitedThisMonth = payMonthOfDate(r.doe) === ym;
    let reasonCode = ESIC_REASON_CODES.WITHOUT_REASON;
    let lastWorkingDay = "";
    // Instruction 6: part-month exit with ≥1 day paid → do not put LWD.
    // Zero days/wages in contribution month with exit → Left Service + LWD.
    if (days === 0 && wages === 0 && exitedThisMonth && r.doe) {
      reasonCode = ESIC_REASON_CODES.LEFT_SERVICE;
      lastWorkingDay = formatEsicLastWorkingDay(r.doe);
    } else if (days === 0 && wages === 0) {
      reasonCode = ESIC_REASON_CODES.ON_LEAVE;
    }
    upsert({
      ipNumber: ip,
      ipName: r.name,
      days,
      monthlyWages: wages,
      reasonCode,
      lastWorkingDay,
      isExit: reasonCode === ESIC_REASON_CODES.LEFT_SERVICE,
      empCode: r.empCode,
    });
  }

  const addLeftService = (r) => {
    const ip = formatEsicIpNumber(r.esiNo || r.ipNumber);
    if (!ip || ip.length < 10) return;
    if (byIp.has(ip)) return; // already in current contribution month
    const doe = r.doe || r.relievedDate || null;
    if (!doe) return;
    if (prevYm && payMonthOfDate(doe) !== prevYm) return;
    upsert({
      ipNumber: ip,
      ipName: r.name || r.ipName,
      days: 0,
      monthlyWages: 0,
      reasonCode: ESIC_REASON_CODES.LEFT_SERVICE,
      lastWorkingDay: formatEsicLastWorkingDay(doe),
      isExit: true,
      empCode: r.empCode,
    });
  };

  // Employee master exits (relieved in previous month) — primary source
  for (const r of masterExitRows || []) addLeftService(r);

  // Prior payroll ESI rows with DOE in previous month (covers register DOE when master missed)
  for (const r of previousEsiRows || []) addLeftService(r);

  return Array.from(byIp.values()).sort((a, b) =>
    String(a.ipNumber).localeCompare(String(b.ipNumber))
  );
}

export function buildPtRows(registerRows = []) {
  return (registerRows || [])
    .map((r) => {
      const amt = ptAmount(r);
      return {
        empCode: r.empCode || "",
        name: r.name || "",
        branch: r.branch || "",
        state: r.branchState || "Unknown",
        paidDays: Number(r.paidDays) || 0,
        grossPay: round2(r.grossPay),
        pt: amt,
      };
    })
    .filter((r) => r.pt > 0.009);
}

export function sumField(rows, key) {
  return round2((rows || []).reduce((s, r) => s + (Number(r[key]) || 0), 0));
}

export function sumFieldRupee(rows, key) {
  return roundRupee(sumField(rows, key));
}

/** EPFO Unified ECR text — 11 fields separated by `#~#`, no header. */
export const EPFO_ECR_DELIMITER = "#~#";

export function buildEpfoEcrText(pfRows = []) {
  const lines = [];
  for (const r of pfRows) {
    const uan = String(r.uan || "").replace(/\D/g, "");
    if (!uan || uan.length < 12) continue;
    const name = String(r.name || "")
      .replace(/#~#/g, " ")
      .replace(/#/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toUpperCase()
      .slice(0, 85);
    const gross = roundRupee(r.grossPay);
    const epfWages = roundRupee(r.pfWages);
    // Do not fall back to EPF wages when EPS is off (epsWages intentionally 0)
    const epsWages = roundRupee(r.epsApplicable === true ? Number(r.epsWages ?? epfWages) || 0 : 0);
    const edliWages = roundRupee(Number(r.edliWages ?? epfWages) || 0);
    const ee = roundRupee(r.employeePf);
    const eps = roundRupee(r.epsApplicable === true ? r.eps : 0);
    const er = roundRupee(r.employerEpf);
    const ncp = Math.max(0, Math.round(Number(r.lopDays) || 0));
    const refund = 0;
    lines.push(
      [uan, name, gross, epfWages, epsWages, edliWages, ee, eps, er, ncp, refund].join(
        EPFO_ECR_DELIMITER
      )
    );
  }
  return lines.join("\n") + (lines.length ? "\n" : "");
}

/** Sum of LOP reversal + salary revision arrears on a register row. */
export function sumArrearsEarnings(row = {}) {
  let revision = round2(Number(row.revisionArrears) || 0);
  let lop = round2(Number(row.lopReversalAmount) || 0);
  if (revision < 0.01) {
    revision = amountByName(row.paidByName, [
      "salary increment arrears",
      "salary revision arrears",
    ]);
  }
  if (lop < 0.01) {
    lop = amountByName(row.paidByName, ["lop reversal"]);
  }
  return {
    revisionArrears: revision,
    lopReversal: lop,
    total: round2(Math.max(0, revision) + Math.max(0, lop)),
  };
}

/**
 * Employer PF split for arrears wages — mirrors backend employerStatutoryBreakdown
 * (Total ER 12%, EPS from rate then EPF = Total − EPS).
 */
export function computePfSharesOnWage(pfWageMonthly = 0, flags = {}, pfRates = {}) {
  const pfApplicable = flags.pfApplicable !== false;
  const ceilingPf = flags.ceilingPf !== false;
  const epsApplicable = flags.epsApplicable === true;
  const pfWage = Math.max(0, Number(pfWageMonthly) || 0);
  const pfWageCeiling = Number(pfRates.pfWageCeiling ?? pfRates.ceiling ?? 15000) || 15000;
  const epsWageCeiling = Number(pfRates.epsWageCeiling ?? 15000) || 15000;
  const edliWageCeiling = Number(pfRates.edliWageCeiling ?? 15000) || 15000;
  const employeeSharePct = Number(pfRates.employeeShare ?? 12) / 100;
  const employerSharePct = Number(pfRates.employerShare ?? 12) / 100;
  const epsRate = Number(pfRates.eps ?? 8.33);
  const epsAmountCap = roundRupee((epsWageCeiling * epsRate) / 100);

  const wageForPfCap = ceilingPf ? Math.min(pfWage, pfWageCeiling) : pfWage;
  const wageForEps = Math.min(pfWage, epsWageCeiling);
  const wageForEdli = Math.min(pfWage, edliWageCeiling);

  if (!pfApplicable || pfWage <= 0) {
    return {
      pfWages: 0,
      epsWages: 0,
      edliWages: 0,
      employeePf: 0,
      employerEpf: 0,
      eps: 0,
      employerShare: 0,
    };
  }

  const employeePf = roundRupee(wageForPfCap * employeeSharePct);
  let employerShare12 = roundRupee(wageForPfCap * employerSharePct);
  let eps = 0;
  if (epsApplicable && wageForEps > 0) {
    eps = Math.min(roundRupee((wageForEps * epsRate) / 100), epsAmountCap);
    if (eps > employerShare12) eps = employerShare12;
  }
  const employerEpf = roundRupee(employerShare12 - eps);

  return {
    pfWages: round2(wageForPfCap),
    epsWages: epsApplicable ? round2(wageForEps) : 0,
    edliWages: round2(wageForEdli),
    employeePf,
    employerEpf,
    eps,
    employerShare: employerShare12,
  };
}

/**
 * PF arrears rows from LOP reversal + salary revision arrears only.
 * Does not alter Regular statement math.
 */
export function buildPfArrearRows(registerRows = [], pfRates = {}) {
  return (registerRows || [])
    .map((r) => {
      const arrears = sumArrearsEarnings(r);
      if (arrears.total < 0.01) return null;
      const flags = r.statutoryFlags || {};
      if (flags.pfApplicable === false) return null;
      const shares = computePfSharesOnWage(arrears.total, flags, pfRates);
      if (shares.employeePf < 0.01 && shares.employerShare < 0.01) return null;
      return {
        empCode: r.empCode || "",
        name: String(r.name || "").replace(/#/g, " ").trim(),
        uan: String(r.uan || "").replace(/\D/g, ""),
        pfAccountNo: r.pfAccountNo || "",
        branch: r.branch || "",
        paidDays: Number(r.paidDays) || 0,
        calendarDays: Number(r.calendarDays) || 0,
        lopDays: Number(r.lopDays) || 0,
        grossPay: arrears.total,
        revisionArrears: arrears.revisionArrears,
        lopReversal: arrears.lopReversal,
        pfWages: shares.pfWages,
        epsWages: shares.epsWages,
        edliWages: shares.edliWages,
        employeePf: shares.employeePf,
        employerEpf: shares.employerEpf,
        eps: shares.eps,
        edli: 0,
        adminCharges: 0,
        employerShare: shares.employerShare,
        totalPf: round2(shares.employeePf + shares.employerShare),
        pfApplicable: true,
        epsApplicable: flags.epsApplicable === true,
        edliApplicable: false,
        isArrears: true,
      };
    })
    .filter(Boolean);
}

/**
 * EPFO Arrear ECR — 8 fields separated by `#~#` (no header):
 * UAN, MEMBER_NAME, ARREAR_EPF_WAGES, ARREAR_EPS_WAGES, ARREAR_EDLI_WAGES,
 * ARREAR_EPF_EE_SHARE, ARREAR_EPF_ER_SHARE, ARREAR_EPS_SHARE
 */
export function buildEpfoArrearEcrText(pfArrearRows = []) {
  const lines = [];
  for (const r of pfArrearRows) {
    const uan = String(r.uan || "").replace(/\D/g, "");
    if (!uan || uan.length < 12) continue;
    const name = String(r.name || "")
      .replace(/#~#/g, " ")
      .replace(/#/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toUpperCase()
      .slice(0, 85);
    const epfWages = roundRupee(r.pfWages);
    const epsWages = roundRupee(r.epsApplicable === true ? Number(r.epsWages ?? epfWages) || 0 : 0);
    const edliWages = roundRupee(Number(r.edliWages ?? epfWages) || 0);
    const ee = roundRupee(r.employeePf);
    const er = roundRupee(r.employerEpf);
    const eps = roundRupee(r.epsApplicable === true ? r.eps : 0);
    lines.push(
      [uan, name, epfWages, epsWages, edliWages, ee, er, eps].join(EPFO_ECR_DELIMITER)
    );
  }
  return lines.join("\n") + (lines.length ? "\n" : "");
}

/**
 * ESI arrears rows — same shape as Regular ESI (for Statement + MC Return).
 * Gross / wages = LOP reversal + salary revision arrears; contributions from rates.
 */
export function buildEsiArrearRows(registerRows = [], esiRates = {}) {
  const eePct = Number(esiRates.employeeShare ?? esiRates.employee ?? 0.75) / 100;
  const erPct = Number(esiRates.employerShare ?? esiRates.employer ?? 3.25) / 100;
  return (registerRows || [])
    .map((r) => {
      const arrears = sumArrearsEarnings(r);
      if (arrears.total < 0.01) return null;
      const flags = r.statutoryFlags || {};
      if (!flags.esiApplicable) return null;
      const wages = arrears.total;
      const employeeEsi = roundRupee(wages * eePct);
      const employerEsi = roundRupee(wages * erPct);
      if (employeeEsi < 0.01 && employerEsi < 0.01) return null;
      return {
        empCode: r.empCode || "",
        name: r.name || "",
        esiNo: String(r.esiNo || "").trim(),
        branch: r.branch || "",
        paidDays: Number(r.paidDays) || 0,
        lopDays: Number(r.lopDays) || 0,
        grossPay: wages,
        revisionArrears: arrears.revisionArrears,
        lopReversal: arrears.lopReversal,
        employeeEsi,
        employerEsi,
        totalEsi: round2(employeeEsi + employerEsi),
        doe: r.doe ? String(r.doe).slice(0, 10) : null,
        isArrears: true,
      };
    })
    .filter(Boolean);
}

/** ESIC monthly contribution rows for portal Excel (legacy helper). */
export function buildEsicPortalRows(esiRows = []) {
  return (esiRows || [])
    .filter((r) => String(r.esiNo || "").trim())
    .map((r) => ({
      ipNumber: String(r.esiNo || "").trim(),
      ipName: r.name || "",
      days: roundRupee(r.paidDays),
      monthlyWages: roundRupee(r.grossPay),
      employeeContribution: roundRupee(r.employeeEsi),
      employerContribution: roundRupee(r.employerEsi),
      reasonCode: "",
    }));
}

/**
 * Download official ESIC MC_Template11.xls with data filled via backend Excel COM
 * so cell colors, fonts, hyperlinks, and Sheet2 stay 100% identical to the template.
 */
export async function downloadEsicMcTemplateFile(filename, mcRows = []) {
  const { payrollOpsService } = await import("../../../services/api");
  const fileName = filename.endsWith(".xls") ? filename : `${filename}.xls`;
  let blob;
  try {
    blob = await payrollOpsService.downloadEsicMcTemplate(mcRows || [], fileName);
  } catch (err) {
    // Axios + responseType:blob — error body may be a Blob of JSON
    const data = err?.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        throw new Error(parsed?.message || err.message || "ESIC MC template download failed");
      } catch (inner) {
        if (inner?.message && !/Unexpected token|JSON/.test(inner.message)) throw inner;
      }
    }
    throw new Error(
      err?.response?.data?.message || err?.message || "ESIC MC template download failed"
    );
  }

  if (blob && blob.type && String(blob.type).includes("application/json")) {
    const text = await blob.text();
    let message = "ESIC MC template download failed";
    try {
      message = JSON.parse(text)?.message || message;
    } catch {
      /* keep default */
    }
    throw new Error(message);
  }

  downloadBlob(fileName, blob);
  return {
    rowCount: (mcRows || []).length,
    exitCount: (mcRows || []).filter(
      (r) => Number(r.reasonCode) === ESIC_REASON_CODES.LEFT_SERVICE
    ).length,
  };
}

/** Group PT rows by state for portal-friendly sheets. */
export function groupPtByState(ptRows = []) {
  const map = {};
  for (const r of ptRows || []) {
    const state = String(r.state || "Unknown").trim() || "Unknown";
    if (!map[state]) map[state] = [];
    map[state].push({
      ...r,
      grossPay: roundRupee(r.grossPay),
      pt: roundRupee(r.pt),
    });
  }
  return map;
}

export function downloadTextFile(filename, content, mime = "text/plain;charset=utf-8") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
